// ═══════════════════════════════════════════════════════════════════════════
// EAGER — Phase-1 Verified Colleges Dataset (Apr 27, 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Source: /app/memory/emergent_phase1_hyderabad_bengaluru_mapping.md
// Reviewed by: ChatGPT (Emergent) + Antigravity AI cross-check
//
// Editorial principles for this dataset:
//   1. NO fabricated placement averages, "highest LPA", or recruiter lists.
//      Use `industry_links` (qualitative) + `source_url` (citation) instead.
//   2. Every entry has `is_verified: true` and at least ONE `source_url`.
//   3. Phase 1 = Hyderabad primary + Bengaluru secondary, design vertical.
//   4. Training partners (MAAC, Arena, etc.) flagged via `is_training_partner`
//      so the UI can render them in a separate "Skill & Training" section.
//
// Schema reference for each entry — see ./colleges.schema.md (TODO)
// ═══════════════════════════════════════════════════════════════════════════

// ─── Category enums ────────────────────────────────────────────────────────
//  Used by the Colleges page filter chips and course→college matching.
export const COLLEGE_CATEGORIES = [
    'Fashion Design',
    'Film & Media',
    'Animation & VFX',
    'Game Design',
    'UX/UI Design',
    'Graphic & Communication',
    'Interior & Spatial',
    'Fine Arts',
    'Jewellery & Accessory',
    'Photography',
    'Architecture',
    'Multimedia',
];

// ─── Partner priority (used for sorting + badge rendering) ─────────────────
export const PARTNER_PRIORITY = {
    TIER_1_ANCHOR:           'tier_1_anchor',
    TIER_2_STRONG:           'tier_2_strong',
    TIER_3_TRAINING_PARTNER: 'tier_3_training_partner',
};

// ─── The dataset ───────────────────────────────────────────────────────────
export const colleges = [

    // ════════════ HYDERABAD — TIER 1 ANCHORS ═══════════════════════════════

    {
        id: 'jnafau-hyderabad',
        name: 'Jawaharlal Nehru Architecture and Fine Arts University',
        short_name: 'JNAFAU',
        slug: 'jnafau-hyderabad',
        area: 'Masab Tank',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_1_anchor',
        ownership: 'State Government University',
        category: ['Fine Arts', 'Animation & VFX', 'Graphic & Communication', 'Interior & Spatial', 'Architecture', 'Photography'],
        course_clusters: ['Animation and VFX', 'Fine Arts', 'Applied Art and Visual Communication', 'Photography and Visual Communication', 'Interior Design', 'Architecture and Planning'],
        courses_to_show: [
            'BFA Animation and Visual Communication',
            'BFA Applied Art and Visual Communication',
            'BFA Photography and Visual Communication',
            'BFA Painting / Sculpture',
            'B.Des Interior Design',
            'B.Arch',
            'M.Arch Interior Design / Environmental Design',
        ],
        qualification: ['10+2 / Intermediate or equivalent', 'B.Arch requires 10+2 with PCM + NATA / JEE Paper 2'],
        entrance: 'JNAFAU Entrance / FADEE',
        industry_links: 'Government design university — most affordable, highest credibility for Telangana students.',
        best_for: 'Affordable, degree-led design careers with a government brand.',
        source_url: [
            'https://jnafau.ac.in/afa_courses/bachelors-of-fine-arts-animation/animation/',
            'https://jnafau.ac.in/programmes/b-design-interior-design/',
        ],
        notes: 'High trust, but admissions are entrance-driven. Position CTA as "guidance and preparation", not guaranteed admission.',
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'nift-hyderabad',
        name: 'National Institute of Fashion Technology, Hyderabad',
        short_name: 'NIFT Hyderabad',
        slug: 'nift-hyderabad',
        area: 'Madhapur',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_1_anchor',
        ownership: 'Government / Statutory Institute (Ministry of Textiles)',
        category: ['Fashion Design', 'Graphic & Communication', 'Jewellery & Accessory'],
        course_clusters: ['Fashion Design', 'Textile Design', 'Fashion Communication', 'Knitwear Design', 'Fashion and Lifestyle Accessories', 'Apparel Production / Fashion Technology'],
        courses_to_show: [
            'B.Des Fashion Design',
            'B.Des Fashion and Lifestyle Accessories',
            'B.Des Fashion Communication',
            'B.Des Knitwear Design',
            'B.Des Textile Design',
            'B.FTech Apparel Production',
        ],
        qualification: [
            'B.Des: 10+2 from a recognized board, any stream',
            'B.FTech: 10+2 with Physics and Mathematics',
            'Max age 24 years on 1 Aug of admission year (relaxation for reserved categories)',
        ],
        entrance: 'NIFT Entrance Exam',
        industry_links: 'Government brand. Positioned as aspirational fashion institute and entrance pathway.',
        best_for: 'Students aiming for the most credible national fashion design degree.',
        source_url: [
            'https://www.nift.ac.in/hyderabad/ug-programmes',
            'https://www.nift.ac.in/apply-ug-programme',
        ],
        notes: 'NIFT Hyderabad runs a Continuing Education (CE) section — surface only after verifying current CE brochure each cycle.',
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'annapurna-college-film-media',
        name: 'Annapurna College of Film and Media',
        short_name: 'Annapurna College',
        slug: 'annapurna-college-film-media',
        area: 'Banjara Hills / Jubilee Hills',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_1_anchor',
        ownership: 'Private — studio-backed film school (Annapurna Studios)',
        category: ['Film & Media', 'Animation & VFX'],
        course_clusters: ['Filmmaking', 'Direction and Screenwriting', 'Cinematography', 'Editing', 'Sound Design', 'Acting', 'VFX and Compositing', '3D Animation'],
        courses_to_show: [
            'BA (Hons) Filmmaking',
            'MA Filmmaking',
            'Certificate Course in Filmmaking',
            'Certificate Course in Film Acting',
            'Certificate Course in 3D Animation',
            'Certificate Course in 3D Animation and VFX',
            'Certificate Course in Advanced Compositing',
            'Certificate Course in Screenwriting',
        ],
        qualification: [
            'BA (Hons) Filmmaking: 10+2 / Intermediate or equivalent',
            'MA Filmmaking: graduation / bachelor degree',
            'Advanced Compositing certificate: minimum 18 years',
        ],
        entrance: 'College selection process (application + portfolio / interview)',
        industry_links: 'Strong studio integration via Annapurna Studios — film school inside a working studio campus.',
        best_for: 'Serious film aspirants who want studio-adjacent learning.',
        source_url: [
            'https://www.acfm.edu.in/',
            'https://www.acfm.edu.in/programs/ba-filmmaking',
            'https://www.acfm.edu.in/certificates/advanced-vfx-compositing-course',
        ],
        notes: 'Avoid quoting placement numbers. Mention studio ecosystem qualitatively only.',
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'iacg-hyderabad',
        name: 'IACG Multimedia College',
        short_name: 'IACG',
        slug: 'iacg-multimedia-college',
        area: 'Dilsukhnagar / Madhapur',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_1_anchor',
        ownership: 'Private — creative media college',
        category: ['Animation & VFX', 'Game Design', 'Film & Media', 'Graphic & Communication', 'UX/UI Design', 'Multimedia'],
        course_clusters: ['Animation', 'VFX', 'Gaming / Game Art / Game Development', 'Film Making', 'Graphic Design', 'UI/UX', 'Art and Design', 'AR/VR'],
        courses_to_show: [
            'BA (Hons) Multimedia — 4 years',
            'BA (Hons) Visual Arts — 3 years',
            'MA Multimedia — 2 years',
            'Professional Diploma in Multimedia / Advanced Multimedia',
            'Specialisations: Animation, VFX, Gaming, Film Making, Graphic Design, UI/UX, AR/VR',
        ],
        qualification: [
            'Bachelor programmes: Intermediate / 10+2',
            'Master programmes: any degree',
            'Diploma programmes: 10th or Intermediate (course-dependent)',
        ],
        entrance: 'College counselling / admission process',
        industry_links: 'Premier local powerhouse for animation, VFX and gaming careers.',
        best_for: 'Animation + VFX + gaming career-track students in Hyderabad.',
        source_url: [
            'https://courses.iacg.info/',
            'https://courses.iacg.info/microsite_bachelors/',
            'https://courses.iacg.info/microsite_diploma/',
        ],
        notes: 'Eager primary partner for VFX/gaming/animation. Confirm latest selection steps before publishing fees.',
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'hamstech-hyderabad',
        name: 'Hamstech Institute of Creative Education',
        short_name: 'Hamstech',
        slug: 'hamstech',
        area: 'Multiple — Jubilee Hills, Punjagutta, Secunderabad',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_1_anchor',
        ownership: 'Private — creative education institute',
        category: ['Fashion Design', 'Interior & Spatial', 'Jewellery & Accessory', 'Photography'],
        course_clusters: ['Fashion Design', 'Interior Design', 'Jewellery Design', 'Photography', 'Makeup Artistry'],
        courses_to_show: [
            'BA (Hons) Fashion Design',
            'BA (Hons) Interior Design',
            'Diploma in Jewellery Design',
            'Diploma in Fashion Design',
            'Diploma in Photography',
        ],
        qualification: [
            'BA (Hons): 10+2 / Intermediate, any stream',
            'Diploma / Certificate: 10th pass / Open',
        ],
        entrance: 'Direct admission / college-level counselling',
        industry_links: 'Largest local fashion + jewellery design brand in Hyderabad with multiple centres.',
        best_for: 'Fast-track fashion / jewellery / interior careers — short and long-term options.',
        source_url: [
            'https://www.hamstech.com/',
            'https://www.hamstech.com/jewellery-design-courses',
        ],
        notes: 'High likelihood of agreeing to commission structures for short-term diplomas. Strong jewellery niche.',
        is_verified: true,
        is_training_partner: false,
    },

    // ════════════ HYDERABAD — TIER 2 STRONG ════════════════════════════════

    {
        id: 'woxsen-hyderabad',
        name: 'Woxsen University — School of Arts and Design',
        short_name: 'Woxsen University',
        slug: 'woxsen-school-of-arts-design',
        area: 'Kamkole',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_2_strong',
        ownership: 'Private University',
        category: ['Fashion Design', 'Interior & Spatial', 'Graphic & Communication', 'UX/UI Design'],
        course_clusters: ['Fashion Design', 'Interior Design', 'Industrial Design', 'Communication Design', 'Product Design Innovation'],
        courses_to_show: [
            'B.Des (Hons) Fashion Design',
            'B.Des (Hons) Interior Design',
            'B.Des (Hons) Industrial Design',
            'B.Des (Hons) Communication Design',
            'M.Des Product Design Innovation',
        ],
        qualification: [
            'B.Des: 10+2 from recognized board (any stream), minimum aggregate 55%',
            'Good understanding of English',
        ],
        entrance: 'Woxsen Design Test (WDT) / NID / NIFT / UCEED / SAT + interview',
        industry_links: 'Premium private B.Des with residential campus and modern design infrastructure.',
        best_for: 'Premium private design degree seekers with budget for residential study.',
        source_url: [
            'https://woxsen.edu.in/academics/programs/b-des-hons',
            'https://woxsen.edu.in/academics/programs/b-des-hons/eligibility/',
        ],
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'icat-hyderabad',
        name: 'ICAT Design and Media College, Hyderabad',
        short_name: 'ICAT Hyderabad',
        slug: 'icat-design-media-hyderabad',
        area: 'L.B. Nagar / Bahadurguda',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_2_strong',
        ownership: 'Private — design and media college',
        category: ['Animation & VFX', 'Game Design', 'UX/UI Design', 'Photography', 'Multimedia'],
        course_clusters: ['Animation', 'Visual Effects', 'Game Art and Design', 'Game Programming', 'Media Technology / Multimedia', 'UI/UX', 'Photography'],
        courses_to_show: [
            'B.Sc Animation',
            'B.Sc Visual Effects',
            'B.Sc Game Art and Design',
            'B.Sc Game Programming',
            'B.Sc Media Technology / Multimedia',
            'B.Sc Photography',
            'B.Sc UI/UX Design',
        ],
        qualification: ['UG: +2 / 10+2 or equivalent', 'PG: relevant bachelor degree (verify by programme)'],
        entrance: 'Application + admission interview / counselling',
        industry_links: 'Niche specialisation pathways — strongest for game art and visual effects.',
        best_for: 'Students wanting degree + creative-media specialisation outside the typical B.Des track.',
        source_url: [
            'https://www.icat.ac.in/',
            'https://www.icat.ac.in/hyderabad/bsc-ba-animation-vfx-degree-college.aspx',
            'https://www.icat.ac.in/hyderabad/bsc-ui-ux-design-degree-college.aspx',
        ],
        notes: 'Brand reputation is mixed vs. Tier-1 — verify campus infrastructure before high-volume push.',
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'cmcfa-hyderabad',
        name: 'Creative Multimedia College of Fine Arts',
        short_name: 'CMCFA',
        slug: 'creative-multimedia-college',
        area: 'Dilsukhnagar',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_2_strong',
        ownership: 'Private (JNAFAU-affiliated)',
        category: ['Animation & VFX', 'Graphic & Communication', 'UX/UI Design', 'Fine Arts', 'Multimedia'],
        course_clusters: ['Animation and VFX', 'Graphic Design', 'UI/UX Design', 'Visual Arts', 'Multimedia'],
        courses_to_show: [
            'BA (Hons) Visual Arts — Animation and VFX',
            'BA (Hons) Design — Graphic Design',
            'BA (Hons) UI / UX Design',
            'MA Multimedia',
        ],
        qualification: ['UG: 10+2 or equivalent', 'PG: bachelor degree'],
        entrance: 'College / JNAFAU process — verify current intake and route each year',
        industry_links: 'JNAFAU-affiliated — government degree credibility with private execution.',
        best_for: 'Students searching for animation / VFX / graphic / UI-UX degrees locally.',
        source_url: [
            'https://www.bestfineartscollege.com/',
            'https://www.bestfineartscollege.com/bachelor-degree-in-visual-arts-animation-vfx.html',
            'https://www.bestmultimedia.com/',
        ],
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'backstage-pass-hyderabad',
        name: 'Backstage Pass Institute of Gaming and Technology',
        short_name: 'Backstage Pass',
        slug: 'backstage-pass-gaming',
        area: 'Madhapur',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_2_strong',
        ownership: 'Private — gaming institute (JNAFAU-affiliated bachelor programmes)',
        category: ['Game Design', 'Animation & VFX'],
        course_clusters: ['Game Art and Design', 'Computer Science and Game Development', 'Augmented Reality and Virtual Reality', 'Game Technology'],
        courses_to_show: [
            'BA (Hons) Game Art and Design',
            'B.Sc (Hons) Computer Science and Game Development',
            'BA (Hons) Augmented Reality and Virtual Reality',
            'M.Sc Game Technology / 3D Game Art and Game Design',
            'Advanced Diploma in Game Art and Design (1 year)',
            'Advanced Diploma in Game Development (1 year)',
            'Advanced Diploma in AR/VR (1 year)',
        ],
        qualification: [
            'BA Game Art and Design: 10+2 or equivalent',
            'B.Sc Computer Science and Game Development: 10+2 with MPC',
            'Advanced Diploma: 10+2 or graduation',
        ],
        entrance: 'Bachelor: entrance + counselling; Diploma: application / counselling',
        industry_links: 'India\'s most established gaming-specific institute — strong industry recognition in game studios.',
        best_for: 'Students who explicitly want game-development or game-art careers.',
        source_url: [
            'https://www.backstagepass.co.in/courses/bachelors-in-game-art-and-design/',
            'https://www.backstagepass.co.in/courses/bachelors-in-computer-science-and-game-development/',
            'https://www.backstagepass.co.in/admissions/admission-process/',
        ],
        notes: 'Niche partner — do not over-position for general design.',
        is_verified: true,
        is_training_partner: false,
    },

    // ════════════ HYDERABAD — GOVT ANCHOR (added per Antigravity review) ═══

    {
        id: 'hcu-school-of-arts',
        name: 'University of Hyderabad — S.N. School of Arts and Communication',
        short_name: 'HCU — S.N. School of Arts',
        slug: 'hcu-school-of-arts',
        area: 'Gachibowli',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_1_anchor',
        ownership: 'Central Government University (UoH)',
        category: ['Fine Arts', 'Film & Media', 'Photography', 'Graphic & Communication'],
        course_clusters: ['Fine Arts', 'Painting', 'Sculpture', 'Print Making', 'Photography', 'Communication', 'Theatre'],
        courses_to_show: [
            'MFA Painting',
            'MFA Sculpture',
            'MFA Print Making',
            'MFA Photography',
            'MA Communication',
            'MA Theatre Arts',
        ],
        qualification: ['Bachelor degree in relevant arts / design field for MFA / MA programmes'],
        entrance: 'UoH entrance exam',
        industry_links: 'India\'s most respected central-university Fine Arts MFA — premier academic credibility.',
        best_for: 'Graduates aspiring to a Fine Arts / Communication MFA at India\'s top central university.',
        source_url: [
            'https://www.uohyd.ac.in/',
            'https://snsac.uohyd.ac.in/',
        ],
        notes: 'PG-only school — surface only to bachelor-degree holders.',
        is_verified: true,
        is_training_partner: false,
    },

    // ════════════ BENGALURU — TIER 1 ANCHORS ═══════════════════════════════

    {
        id: 'srishti-manipal-bengaluru',
        name: 'Srishti Manipal Institute of Art, Design and Technology',
        short_name: 'Srishti Manipal',
        slug: 'srishti-manipal-bengaluru',
        area: 'Yelahanka',
        city: 'Bengaluru',
        state: 'Karnataka',
        city_priority: 'secondary',
        partner_priority: 'tier_1_anchor',
        ownership: 'Private (MAHE — Manipal Academy of Higher Education)',
        category: ['Graphic & Communication', 'UX/UI Design', 'Interior & Spatial', 'Film & Media', 'Fashion Design', 'Multimedia'],
        course_clusters: ['Human Centered Design', 'Visual Communication and Strategic Branding', 'Information Arts and Information Design', 'Spatial Design', 'Industrial Arts and Design Practices', 'Creative and Applied Computation', 'Digital Media Arts', 'Film', 'Textile Futures'],
        courses_to_show: [
            'B.Des Business Services and Systems Design',
            'B.Des Creative and Applied Computation',
            'B.Des Human Centered Design',
            'B.Des Industrial Arts and Design Practices',
            'B.Des Information Arts and Information Design Practices',
            'B.Des Spatial Design',
            'B.Des Textile Futures',
            'B.Des Visual Communication and Strategic Branding',
            'BFA Digital Media Arts',
            'BFA Film',
            'M.Des / MA Design and Arts programmes',
        ],
        qualification: [
            '4-year UG: 10+2 with minimum 45% aggregate',
            'PG: recognized undergraduate degree',
        ],
        entrance: 'Srishti / MAHE selection — application + portfolio / interview',
        industry_links: 'Top-tier transdisciplinary design school in India — premier private B.Des destination in Bengaluru.',
        best_for: 'Serious design students seeking transdisciplinary, high-quality private undergraduate design.',
        source_url: [
            'https://srishtimanipalinstitute.in/admissions/eligibility/',
            'https://srishtimanipalinstitute.in/academics-overview/programs-courses/4-year-ug-professional-program/',
        ],
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'nid-bengaluru',
        name: 'National Institute of Design, Bengaluru',
        short_name: 'NID Bengaluru',
        slug: 'nid-bengaluru',
        area: 'Yeshwanthpur',
        city: 'Bengaluru',
        state: 'Karnataka',
        city_priority: 'secondary',
        partner_priority: 'tier_1_anchor',
        ownership: 'Government — NID (Ahmedabad campus extension)',
        category: ['UX/UI Design', 'Game Design', 'Graphic & Communication'],
        course_clusters: ['Interaction Design', 'Digital Game Design', 'Information Design', 'Universal Design', 'Design for Retail Experience'],
        courses_to_show: [
            'M.Des Interaction Design',
            'M.Des Digital Game Design',
            'M.Des Information Design',
            'M.Des Universal Design',
            'M.Des Design for Retail Experience',
        ],
        qualification: ['Bachelor degree / diploma per NID DAT M.Des handbook'],
        entrance: 'NID DAT M.Des',
        industry_links: 'India\'s most prestigious design school — government brand, world-class faculty.',
        best_for: 'Bachelor-degree holders aiming for the most credible Indian design master\'s.',
        source_url: [
            'https://nid.edu/about/campuses/bengaluru-campus',
            'https://www.nid.edu/academics/programmes/master-of-design-mdes/digital-game-design-mdes',
            'https://www.nid.edu/academics/programmes/master-of-design-mdes/interaction-design-mdes',
        ],
        notes: 'PG-only campus. Do not surface to 12th-pass UG students.',
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'nift-bengaluru',
        name: 'National Institute of Fashion Technology, Bengaluru',
        short_name: 'NIFT Bengaluru',
        slug: 'nift-bengaluru',
        area: 'HSR Layout',
        city: 'Bengaluru',
        state: 'Karnataka',
        city_priority: 'secondary',
        partner_priority: 'tier_1_anchor',
        ownership: 'Government / Statutory Institute (Ministry of Textiles)',
        category: ['Fashion Design', 'Graphic & Communication', 'Jewellery & Accessory'],
        course_clusters: ['Fashion Design', 'Fashion Communication', 'Fashion and Lifestyle Accessories', 'Textile Design', 'Knitwear Design', 'Apparel Production'],
        courses_to_show: [
            'B.Des Fashion Design',
            'B.Des Fashion Communication',
            'B.Des Fashion and Lifestyle Accessories',
            'B.Des Textile Design',
            'B.Des Knitwear Design',
            'B.FTech Apparel Production',
        ],
        qualification: [
            'B.Des: 10+2 (any stream)',
            'B.FTech: 10+2 with Physics and Mathematics',
            'Max age 24 years on 1 Aug of admission year',
        ],
        entrance: 'NIFT Entrance Exam',
        industry_links: 'Government brand — Bengaluru fashion anchor and entrance pathway.',
        best_for: 'Bengaluru fashion design aspirants who want the most credible national degree.',
        source_url: [
            'https://www.nift.ac.in/bengaluru/ug-programmes',
            'https://www.nift.ac.in/apply-ug-programme',
        ],
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'pearl-academy-bengaluru',
        name: 'Pearl Academy, Bengaluru',
        short_name: 'Pearl Academy',
        slug: 'pearl-academy-bengaluru',
        area: 'Whitefield',
        city: 'Bengaluru',
        state: 'Karnataka',
        city_priority: 'secondary',
        partner_priority: 'tier_1_anchor',
        ownership: 'Private — Pearl Academy (multi-campus)',
        category: ['Fashion Design', 'Interior & Spatial', 'Graphic & Communication', 'Jewellery & Accessory', 'UX/UI Design'],
        course_clusters: ['Fashion Design', 'Interior Architecture', 'Communication Design', 'Jewellery Design', 'Fashion Styling and Image Design', 'UX Design'],
        courses_to_show: [
            'BA (Hons) Fashion Design',
            'BA (Hons) Interior Architecture and Design',
            'BA (Hons) Communication Design',
            'BA (Hons) Jewellery Design',
            'BA (Hons) Fashion Styling and Image Design',
            'BA (Hons) UX Design',
        ],
        qualification: ['10+2 / Intermediate', 'Aptitude test + portfolio review'],
        entrance: 'Pearl Academy aptitude test + interview',
        industry_links: 'Premium private design education brand — heavy industry collaborations and international exposure.',
        best_for: 'Students with budget and ambition for a premium, internationally-oriented design degree.',
        source_url: [
            'https://www.pearlacademy.com/bengaluru/',
            'https://www.pearlacademy.com/courses/',
        ],
        is_verified: true,
        is_training_partner: false,
    },

    // ════════════ BENGALURU — TIER 2 STRONG ════════════════════════════════

    {
        id: 'rv-university-bengaluru',
        name: 'RV University — School of Design and Innovation',
        short_name: 'RV University',
        slug: 'rv-university-bengaluru',
        area: 'Mysuru Road',
        city: 'Bengaluru',
        state: 'Karnataka',
        city_priority: 'secondary',
        partner_priority: 'tier_2_strong',
        ownership: 'Private University',
        category: ['UX/UI Design', 'Graphic & Communication', 'Interior & Spatial'],
        course_clusters: ['Product Design', 'Communication and New Media', 'Interior Environments', 'User Experience', 'Transdisciplinary Contexts'],
        courses_to_show: [
            'B.Des (Hons) Product',
            'B.Des (Hons) Communication and New Media',
            'B.Des (Hons) Interior Environments',
            'B.Des (Hons) User Experience',
            'B.Des (Hons) Transdisciplinary Contexts',
        ],
        qualification: ['B.Des: 10+2 (any stream), minimum 60%'],
        entrance: 'UCEED / NID accepted; RVU selection process + interview / portfolio',
        industry_links: 'Rising premium private design programme with strong industry exposure.',
        best_for: 'Students who want a structured private B.Des in Bengaluru beyond Srishti.',
        source_url: [
            'https://sdi.rvu.edu.in/programmes/undergraduate-programmes/b-des-hons/',
            'https://sdi.rvu.edu.in/programmes/undergraduate-programmes/b-des-hons/b-des-hons-user-experience/',
        ],
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'icat-bengaluru',
        name: 'ICAT Design and Media College, Bengaluru',
        short_name: 'ICAT Bengaluru',
        slug: 'icat-design-media-bengaluru',
        area: 'Bommanahalli / Hosur Main Road',
        city: 'Bengaluru',
        state: 'Karnataka',
        city_priority: 'secondary',
        partner_priority: 'tier_2_strong',
        ownership: 'Private — design and media college',
        category: ['Animation & VFX', 'Game Design', 'Fashion Design', 'UX/UI Design', 'Multimedia'],
        course_clusters: ['Visual Effects', 'Game Art and Design', 'Fashion Design', 'Animation', 'UI/UX', 'Multimedia / Media Technology'],
        courses_to_show: [
            'B.Sc Visual Effects',
            'B.Sc Game Art and Design',
            'B.Sc Fashion Design',
            'Animation / Multimedia / UI-UX programmes',
        ],
        qualification: ['UG: +2 / 10+2 or equivalent'],
        entrance: 'Application + admission interview / counselling',
        industry_links: 'Practical media + design pathways with strong placement orientation.',
        best_for: 'Bengaluru students wanting niche specialisation (game art, VFX, fashion) at a Tier-2 college.',
        source_url: [
            'https://www.icat.ac.in/bengaluru/bsc-visual-effects-degree-college.aspx',
            'https://www.icat.ac.in/bengaluru/bsc-game-art-design-degree-college.aspx',
            'https://www.icat.ac.in/bengaluru/bsc-fashion-apparel-design-degree-college.aspx',
        ],
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'ecole-intuit-lab-bengaluru',
        name: 'École Intuit Lab, Bengaluru',
        short_name: 'École Intuit Lab',
        slug: 'ecole-intuit-lab-bengaluru',
        area: 'Indiranagar',
        city: 'Bengaluru',
        state: 'Karnataka',
        city_priority: 'secondary',
        partner_priority: 'tier_2_strong',
        ownership: 'Private — international design school (French origin)',
        category: ['Graphic & Communication', 'UX/UI Design', 'Animation & VFX', 'Game Design'],
        course_clusters: ['Graphic Design and Visual Communication', 'Multimedia and Digital Product Design UX/UI', 'Multimedia Game Art and Design', 'Multimedia Animation and Motion Design'],
        courses_to_show: [
            'Bachelor in Graphic Design and Visual Communication',
            'Bachelor in Multimedia and Digital Product Design (UX/UI)',
            'Bachelor in Multimedia, Game Art and Design',
            'Bachelor in Multimedia, Animation and Motion Design',
        ],
        qualification: ['10+2 or equivalent + interview / portfolio / motivation'],
        entrance: 'Online application + admissions interview + portfolio review',
        industry_links: 'International curriculum and faculty — French design school heritage.',
        best_for: 'Students seeking premium international-style design education with small cohorts.',
        source_url: [
            'https://ecole-intuit-lab.com/about-school/',
            'https://www.ecole-intuit-lab.com/en/admissions/',
        ],
        notes: 'Small cohort — limited annual seats. Premium positioning.',
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'jd-institute-bengaluru',
        name: 'JD Institute of Fashion Technology, Bengaluru',
        short_name: 'JD Institute',
        slug: 'jd-institute-bengaluru',
        area: 'Brigade Road / Residency Road',
        city: 'Bengaluru',
        state: 'Karnataka',
        city_priority: 'secondary',
        partner_priority: 'tier_2_strong',
        ownership: 'Private — design institute',
        category: ['Fashion Design', 'Interior & Spatial', 'Jewellery & Accessory'],
        course_clusters: ['Fashion Design', 'Jewellery Design', 'Interior Design', 'Fashion Communication', 'Visual Merchandising'],
        courses_to_show: [
            'B.Sc in Fashion & Apparel Design',
            'B.Sc in Interior Design',
            'Diploma in Fine Jewellery Design',
            'Diploma in Fashion Styling',
            'Diploma in Visual Merchandising',
        ],
        qualification: [
            'B.Sc: 10+2 (any stream)',
            'Diploma courses: 10th pass / Open',
        ],
        entrance: 'General Aptitude Test (GAT) / direct admission via counselling',
        industry_links: 'Long-running fashion + jewellery institute — strong industry placement track in Bengaluru.',
        best_for: 'Students wanting fast-track fashion / jewellery industry entry without a 4-year degree.',
        source_url: [
            'https://www.jdinstitute.edu.in/',
            'https://www.jdinstitute.edu.in/jewellery-design-courses/',
        ],
        notes: 'Fills the Jewellery / Accessory gap that major universities don\'t cover as flexibly.',
        is_verified: true,
        is_training_partner: false,
    },

    {
        id: 'lisaa-bengaluru',
        name: 'LISAA School of Design, Bengaluru',
        short_name: 'LISAA',
        slug: 'lisaa-bengaluru',
        area: 'JP Nagar',
        city: 'Bengaluru',
        state: 'Karnataka',
        city_priority: 'secondary',
        partner_priority: 'tier_2_strong',
        ownership: 'Private — international design school (French origin)',
        category: ['Fashion Design', 'Interior & Spatial', 'Graphic & Communication', 'UX/UI Design', 'Animation & VFX', 'Game Design'],
        course_clusters: ['Fashion Design', 'Interior Architecture', 'Graphic Design', 'UX/UI', 'Animation and VFX', 'Game Art and Design'],
        courses_to_show: [
            'BA (Hons) Fashion Design',
            'BA (Hons) Interior Architecture and Design',
            'BA (Hons) Graphic and Communication Design',
            'BA (Hons) UI/UX',
            'BA (Hons) Animation and VFX',
            'BA (Hons) Game Art and Design',
        ],
        qualification: ['10+2 / Intermediate + portfolio interview'],
        entrance: 'Application + portfolio + interview',
        industry_links: 'French-origin international curriculum — strong cross-discipline coverage in Bengaluru.',
        best_for: 'Students wanting an internationally flavoured private design degree across multiple disciplines.',
        source_url: ['https://www.lisaadesign.in/'],
        is_verified: true,
        is_training_partner: false,
    },

    // ════════════ TRAINING PARTNERS (TIER 3 — separate UI section) ═════════

    {
        id: 'maac-hyderabad',
        name: 'MAAC Hyderabad',
        short_name: 'MAAC Hyderabad',
        slug: 'maac-hyderabad',
        area: 'Multiple — Madhapur, Ameerpet',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_3_training_partner',
        ownership: 'Private — animation training network',
        category: ['Animation & VFX', 'Game Design', 'Graphic & Communication', 'Multimedia'],
        course_clusters: ['Animation', 'VFX', 'Gaming', 'AR/VR', 'Broadcast', 'Digital Design', 'Graphic / Web / UI'],
        courses_to_show: [
            'Advanced Program in 3D Animation',
            '3D Animation Filmmaking',
            'Animation and Filmmaking',
            'Visual Effects (Program / Advanced)',
            'Compositing and Editing Plus',
            'Game Design / Interactive Design and Games',
            'AR/VR course',
            'Broadcast Design',
            'Digital Media and Design',
        ],
        qualification: ['After class 12 / open skill pathway — confirm centre-level eligibility'],
        entrance: 'Centre-level admission + counselling',
        industry_links: 'Long-running national animation training brand — practical, job-ready focus.',
        best_for: 'Students who want fast-track skill training rather than a 3-4 year degree.',
        source_url: [
            'https://m.maacindia.com/campaign/career-building-courses/index.aspx',
            'https://m.maacindia.com/blog/about-maac',
        ],
        is_verified: true,
        is_training_partner: true,
    },

    {
        id: 'arena-hyderabad',
        name: 'Arena Animation, Hyderabad',
        short_name: 'Arena Animation',
        slug: 'arena-animation-hyderabad',
        area: 'Multiple centres — Hyderabad',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_3_training_partner',
        ownership: 'Private — animation training network',
        category: ['Animation & VFX', 'Game Design', 'UX/UI Design', 'Graphic & Communication'],
        course_clusters: ['Animation', 'VFX', 'Gaming', 'UI/UX', 'Graphic Design', 'Editing', 'Motion Graphics'],
        courses_to_show: [
            'Advanced Program in Animation and VFX',
            'Animation with Unreal Engine',
            'VFX Compositing and Editing',
            '2D Animation',
            'Motion Graphics',
            'UI/UX Design',
            'Game Development',
            'Graphic Design',
        ],
        qualification: ['Many short courses accept 10th pass; long programmes accept 12th / graduate (course-dependent)'],
        entrance: 'Centre-level admission',
        industry_links: 'Practical software-led training — ideal for tool mastery (Maya, Blender, Photoshop, etc.).',
        best_for: 'Working professionals or 12th-pass students wanting industry-ready software skills.',
        source_url: [
            'https://www.arenaanimations.com/',
            'https://www.arenahyderabad.in/short-term-courses.html',
        ],
        is_verified: true,
        is_training_partner: true,
    },

    {
        id: 'maac-bengaluru',
        name: 'MAAC Bengaluru',
        short_name: 'MAAC Bengaluru',
        slug: 'maac-bengaluru',
        area: 'Multiple — Bengaluru centres',
        city: 'Bengaluru',
        state: 'Karnataka',
        city_priority: 'secondary',
        partner_priority: 'tier_3_training_partner',
        ownership: 'Private — animation training network',
        category: ['Animation & VFX', 'Game Design', 'Graphic & Communication', 'Multimedia'],
        course_clusters: ['Animation', 'VFX', '3D', 'Gaming', 'AR/VR', 'Digital Design', 'Graphic / Web / UI'],
        courses_to_show: [
            'Advanced Program in 3D Animation',
            'Animation Filmmaking',
            'Advanced Visual Effects',
            'Compositing and Editing',
            'Game Design',
            'AR/VR',
            'Digital Design / Graphic-Web-2D Animation',
        ],
        qualification: ['After class 12 / open skill pathway — confirm centre-level eligibility'],
        entrance: 'Centre-level admission + counselling',
        industry_links: 'National animation training network with multiple Bengaluru branches.',
        best_for: 'Bengaluru students looking for fast-track animation / VFX / game skills.',
        source_url: ['https://m.maacindia.com/campaign/career-building-courses/index.aspx'],
        is_verified: true,
        is_training_partner: true,
    },

    {
        id: 'arena-bengaluru',
        name: 'Arena Animation, Bengaluru',
        short_name: 'Arena Animation',
        slug: 'arena-animation-bengaluru',
        area: 'Multiple — Bengaluru centres',
        city: 'Bengaluru',
        state: 'Karnataka',
        city_priority: 'secondary',
        partner_priority: 'tier_3_training_partner',
        ownership: 'Private — animation training network',
        category: ['Animation & VFX', 'Game Design', 'UX/UI Design', 'Graphic & Communication'],
        course_clusters: ['Animation', 'VFX', 'Gaming', 'UI/UX', 'Graphic Design', 'Motion Graphics'],
        courses_to_show: [
            'Animation Prime / Animation and VFX',
            'VFX Prime',
            'Game Design',
            'UI/UX Design',
            'Graphics and Web Design',
            'Motion Graphics',
        ],
        qualification: ['Usually 10th / 12th pass or open skill route — verify at centre'],
        entrance: 'Centre-level admission',
        industry_links: 'Software-led practical training across multiple Bengaluru centres.',
        best_for: 'Bengaluru students who want short-term, software-focused skill courses.',
        source_url: ['https://www.arenaanimations.com/'],
        is_verified: true,
        is_training_partner: true,
    },

    {
        id: 'frameboxx-hyderabad',
        name: 'Frameboxx 2.0, Hyderabad',
        short_name: 'Frameboxx',
        slug: 'frameboxx-hyderabad',
        area: 'Verify centre — Hyderabad',
        city: 'Hyderabad',
        state: 'Telangana',
        city_priority: 'primary',
        partner_priority: 'tier_3_training_partner',
        ownership: 'Private — animation / VFX training network',
        category: ['Animation & VFX', 'Game Design', 'UX/UI Design', 'Graphic & Communication', 'Photography'],
        course_clusters: ['3D', 'VFX', 'Animation', 'Graphic Design', 'UI/UX', 'Video Editing', 'Game Art', 'Photography'],
        courses_to_show: [
            'Specialization Program in 3D / VFX (24 months)',
            'Expert Program Graphic Design',
            'Expert Program Web and UI/UX Design',
            'Video Editing',
            'Architecture Visualization',
            'Photography',
            'Virtual Reality / Metaverse',
        ],
        qualification: ['After 12th / career-switcher friendly — verify centre-specific requirement'],
        entrance: 'Centre-level admission',
        industry_links: 'Specialised animation / VFX training brand with long-form intensive programmes.',
        best_for: 'Students wanting 1-2 year deep-dive software training over a 3-4 year degree.',
        source_url: [
            'https://frameboxx.in/',
            'https://frameboxx.in/courses/specialization-program/specialization-program-in-3d-vfx',
        ],
        notes: 'Active Hyderabad centre status should be verified before public-facing publishing.',
        is_verified: true,
        is_training_partner: true,
    },

];

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Filters out any saved-college IDs that no longer exist in the verified
 * dataset.  Used by Profile / Shortlist screens to keep the UI safe after
 * the colleges archive migration.
 */
export function filterValidCollegeIds(savedIds = []) {
    const validIds = new Set(colleges.map(c => c.id));
    return savedIds.filter(id => validIds.has(id));
}

/**
 * Returns the top N colleges that match the given course title or course id.
 * Matching is done against `course_clusters` (case-insensitive token overlap).
 * Training partners are returned LAST so degree colleges always rank above.
 */
export function getCollegesForCourse(courseTitleOrId, { limit = 6 } = {}) {
    const needle = String(courseTitleOrId || '').toLowerCase();
    if (!needle) return [];

    // Hand-curated mapping (course id → preferred college ids, by Antigravity review)
    const CURATED = {
        bdes_graphic:           ['srishti-manipal-bengaluru', 'cmcfa-hyderabad', 'lisaa-bengaluru', 'pearl-academy-bengaluru'],
        bdes_uxui:              ['woxsen-hyderabad', 'srishti-manipal-bengaluru', 'rv-university-bengaluru', 'pearl-academy-bengaluru', 'ecole-intuit-lab-bengaluru'],
        bdes_prod:              ['woxsen-hyderabad', 'srishti-manipal-bengaluru', 'rv-university-bengaluru'],
        // Merged fashion + textile course (Phase 1) — Hamstech (private) first, then NIFT govt colleges only
        bdes_fashion_textile:   ['hamstech-hyderabad', 'nift-hyderabad', 'nift-bengaluru'],
        // Legacy ids preserved so old localStorage / shared URLs still resolve
        bdes_fashion:           ['hamstech-hyderabad', 'nift-hyderabad', 'nift-bengaluru'],
        bdes_textile:           ['nift-hyderabad', 'nift-bengaluru'],
        // Interior — Phase 1 partner-only shortlist
        bdes_interior:          ['hamstech-hyderabad'],
        bdes_comm:              ['srishti-manipal-bengaluru', 'woxsen-hyderabad', 'pearl-academy-bengaluru', 'rv-university-bengaluru'],
        bdes_jewel:             ['hamstech-hyderabad', 'jd-institute-bengaluru', 'nift-hyderabad', 'nift-bengaluru', 'pearl-academy-bengaluru'],
        bsc_anim:               ['iacg-hyderabad', 'cmcfa-hyderabad', 'icat-hyderabad', 'icat-bengaluru', 'lisaa-bengaluru'],
        bsc_game:               ['backstage-pass-hyderabad', 'iacg-hyderabad', 'icat-hyderabad', 'icat-bengaluru', 'lisaa-bengaluru'],
        bsc_film:               ['annapurna-college-film-media'],
        bfa:                    ['jnafau-hyderabad', 'cmcfa-hyderabad', 'hcu-school-of-arts'],
    };

    // 1. If it matches a curated course id, return curated list (preserves order)
    const curatedIds = CURATED[needle];
    if (curatedIds) {
        const idMap = new Map(colleges.map(c => [c.id, c]));
        const list = curatedIds.map(id => idMap.get(id)).filter(Boolean);
        return list.slice(0, limit);
    }

    // 2. Otherwise fall back to fuzzy matching against course_clusters / courses_to_show
    const tokens = needle.split(/[\s/&,_-]+/).filter(t => t.length > 2 && !['the', 'and', 'des', 'bsc', 'bfa', 'design'].includes(t));
    if (tokens.length === 0) return [];

    const scored = colleges.map(c => {
        const haystack = [
            ...(c.course_clusters || []),
            ...(c.courses_to_show || []),
            ...(c.category || []),
        ].join(' ').toLowerCase();
        const score = tokens.reduce((acc, t) => acc + (haystack.includes(t) ? 1 : 0), 0);
        return { college: c, score };
    });

    return scored
        .filter(s => s.score > 0)
        .sort((a, b) => {
            // degree colleges before training partners; then by score
            const trainerDiff = Number(a.college.is_training_partner) - Number(b.college.is_training_partner);
            if (trainerDiff !== 0) return trainerDiff;
            return b.score - a.score;
        })
        .slice(0, limit)
        .map(s => s.college);
}
